/**
 * Institution terminology shared across texts.
 *
 * A template such as `"Selecciona un {{glossary:building}}"` lets the business rename
 * an entity once and have it apply everywhere it is referenced. Resolving one is a
 * DRAFTING aid only: Spanish inflects, so substituting a term can break number and
 * gender agreement ("La capacidad mínima" -> "La aforo mínima"). Every result is
 * therefore classified, and anything not provably clean is flagged for a human to
 * rewrite rather than shipped as-is.
 *
 * Casing is learned rather than assumed: the same entity reads "sede" mid-sentence and
 * "Sede" at the start, and the glossary only stores one form.
 */

import type { LocaleCode } from "./i18nCatalog";

export const GLOSSARY_KEY_PREFIX = "glossary.";

export const GLOSSARY_MAIN_KEY = "glossary";

/** A well-formed reference. The name is the last segment of the glossary key. */
const GLOSSARY_TOKEN = /\{\{glossary:([a-z0-9-]+)\}\}/g;

/** Native i18next nesting used by locale JSON files for grammar-safe references. */
const I18NEXT_GLOSSARY_TOKEN = /\$t\((glossary(?:\.[a-z0-9-]+)+)\)/g;

/** A template that is only the reference, with nothing around it. */
const WHOLE_TERM_TEMPLATE = /^{{glossary:[a-z0-9-]+}}$/;

/** Any opening reference, used to spot leftovers from a botched find and replace. */
const ANY_GLOSSARY_TOKEN = /\{\{glossary:/;

/**
 * A reference wrapping another one, the shape a find and replace leaves behind when it
 * runs over text that was already tokenised: replacing `classroom` with its reference
 * inside `{{glossary:classroom-type}}` yields `{{glossary:{{glossary:classroom}}-type}}`.
 * The trailing group keeps the rest of the name, which belongs to the outer reference.
 */
const NESTED_GLOSSARY_TOKEN = /\{\{glossary:\{\{glossary:([a-z0-9-]+)\}\}([a-z0-9-]*)\}\}/g;

/** A term right after an article inherits its gender and number, which substitution breaks. */
const TRAILING_ARTICLE = /\b(el|la|los|las|un|una|unos|unas|del|al|este|esta|estos|estas)\s+$/i;

const REGEXP_SPECIAL = /[.*+?^${}()|[\]\\]/g;

export interface GlossaryEntry {
  /** Reference name used inside `{{glossary:...}}`. */
  name: string;
  fullKey: string;
  currentEs: string;
  newEs: string;
  currentEn: string;
  newEn: string;
}

export type Glossary = Record<string, GlossaryEntry>;

export type ExpansionStatus =
  /** Resolved and provably aligned with the app text, no agreement risk detected. */
  | "clean"
  /** Resolved, but a human has to check casing, number or gender agreement. */
  | "needs-review"
  /** The template does not match the app text, usually a plural. Cannot be aligned. */
  | "not-aligned"
  /** Nested or unterminated references, e.g. `{{glossary:{{glossary:section}}}}`. */
  | "malformed"
  /** References an entity the glossary sheet does not define. */
  | "unknown-entity";

export interface ExpansionResult {
  status: ExpansionStatus;
  /** Set for every status other than "not-aligned", "malformed" and "unknown-entity". */
  text?: string;
  /** Entity names referenced by the input. */
  entities: string[];
  /** Human readable reason, set for every status other than "clean". */
  reason?: string;
}

/** True when an entity was capitalised at that spot in the app text. */
export type EntityCasing = Record<string, boolean>;

function escapeForRegExp(value: string): string {
  return value.replace(REGEXP_SPECIAL, "\\$&");
}

export function hasGlossaryReference(value: string): boolean {
  return ANY_GLOSSARY_TOKEN.test(value ?? "");
}

/** Resolves locale-file `$t(glossary...)` nesting for catalog display and validation. */
export function resolveI18nextGlossaryReferences(
  value: string,
  flatMessages: Record<string, string>,
): string {
  return (value ?? "").replace(I18NEXT_GLOSSARY_TOKEN, (reference, fullKey: string) => {
    return flatMessages[fullKey] ?? reference;
  });
}

/**
 * Converts a locale-file nesting reference into the compact token used by the business
 * workbook. Returns an empty string when the locale value does not use the glossary.
 */
export function toCatalogGlossaryTemplate(value: string): string {
  let foundReference = false;
  const template = (value ?? "").replace(I18NEXT_GLOSSARY_TOKEN, (_reference, fullKey: string) => {
    foundReference = true;

    return `{{glossary:${fullKey.split(".").pop()}}}`;
  });

  return foundReference ? template : "";
}

/** True when a reference is nested or unterminated, which resolution must never guess at. */
export function hasMalformedGlossaryReference(value: string): boolean {
  return hasGlossaryReference((value ?? "").replace(GLOSSARY_TOKEN, ""));
}

/**
 * Unwraps doubly referenced entities, the only malformation whose intent is unambiguous:
 * the inner reference names the entity and the outer one is the leftover wrapper.
 *
 * Repeats until it settles, since the same text can have been run through the find and
 * replace more than twice. Anything else — an unterminated reference, a name the token
 * pattern does not accept — is left as is for `hasMalformedGlossaryReference` to report.
 */
export function collapseNestedGlossaryReferences(value: string): string {
  let collapsed = value ?? "";
  let previous = "";

  while (collapsed !== previous) {
    previous = collapsed;
    collapsed = collapsed.replace(NESTED_GLOSSARY_TOKEN, "{{glossary:$1$2}}");
  }

  return collapsed;
}

/**
 * True when the text is nothing but the term. The locale files express these through
 * i18next nesting, so overriding the term alone already changes them — which is why the
 * tooling must not draft a literal for such a row and freeze the term out of that.
 */
export function isWholeTermTemplate(template: string): boolean {
  return WHOLE_TERM_TEMPLATE.test((template ?? "").trim());
}

export function getReferencedEntities(template: string): string[] {
  return [...(template ?? "").matchAll(GLOSSARY_TOKEN)].map(match => match[1]);
}

export function getGlossaryValue(
  entry: GlossaryEntry,
  locale: LocaleCode,
  which: "current" | "new",
): string {
  if (locale === "es") return which === "new" ? entry.newEs || entry.currentEs : entry.currentEs;

  return which === "new" ? entry.newEn || entry.currentEn : entry.currentEn;
}

/** Entities whose term changes in this locale — the only ones that can alter a text. */
export function getChangedEntities(glossary: Glossary, locale: LocaleCode): string[] {
  return Object.values(glossary)
    .filter(
      entry =>
        getGlossaryValue(entry, locale, "new") !== getGlossaryValue(entry, locale, "current"),
    )
    .map(entry => entry.name)
    .sort();
}

function isCapitalised(value: string): boolean {
  const first = value?.charAt(0) ?? "";

  return !!first && first === first.toUpperCase() && first !== first.toLowerCase();
}

/**
 * A term carrying its own capitals ("Plazas Ofertadas", "Grupo-Asignatura") cannot be
 * re-cased by touching the first letter alone — doing so yields "plazas Ofertadas".
 */
function hasInternalCapital(value: string): boolean {
  const rest = (value ?? "").slice(1);

  return rest !== rest.toLowerCase();
}

function applyCasing(value: string, shouldCapitalise: boolean): string {
  if (!value) return value;

  return shouldCapitalise
    ? value.charAt(0).toUpperCase() + value.slice(1)
    : value.charAt(0).toLowerCase() + value.slice(1);
}

interface LearnCasingOptions {
  template: string;
  /** The text the app renders today, which the template is supposed to describe. */
  appText: string;
  glossary: Glossary;
  locale: LocaleCode;
}

/**
 * Works out how each referenced entity is cased in the text the app actually renders.
 *
 * The template is turned into a case-insensitive pattern built from the CURRENT terms
 * and matched against the app text. A match proves the template describes that text and
 * reveals the casing of every occurrence. Returns null when it does not match — usually
 * a plural or an accent the glossary spells differently — so callers can fall back
 * instead of inventing a casing.
 */
export function learnEntityCasing({
  template,
  appText,
  glossary,
  locale,
}: LearnCasingOptions): EntityCasing | null {
  if (!template || appText === undefined || appText === null) return null;
  if (hasMalformedGlossaryReference(template)) return null;

  const matches = [...template.matchAll(GLOSSARY_TOKEN)];

  if (matches.some(match => !glossary[match[1]])) return null;

  let pattern = "";
  let cursor = 0;

  matches.forEach(match => {
    const current = getGlossaryValue(glossary[match[1]], locale, "current");

    pattern += escapeForRegExp(template.slice(cursor, match.index));
    pattern += `(${escapeForRegExp(current)})`;
    cursor = match.index + match[0].length;
  });

  pattern += escapeForRegExp(template.slice(cursor));

  const aligned = new RegExp(`^${pattern}$`, "i").exec(appText);

  if (!aligned) return null;

  const casing: EntityCasing = {};

  matches.forEach((match, index) => {
    casing[match[1]] = isCapitalised(aligned[index + 1] ?? "");
  });

  return casing;
}

interface ResolveOptions {
  value: string;
  glossary: Glossary;
  locale: LocaleCode;
  /**
   * Casing learned from the row's template. Entities missing from it are inserted as the
   * glossary spells them and the result is flagged, since their casing is a guess.
   */
  casing?: EntityCasing;
}

/** Names the thing being checked, which is all that differs between the two callers. */
type ReferenceSubject = "El texto" | "La plantilla";

/**
 * The checks `resolveReferences` and `expandTemplate` share: a reference that cannot be
 * read, and one naming an entity the glossary sheet never defined. Returns the result that
 * disqualifies the value, or null when it is safe to go on.
 */
function validateReferences(
  value: string,
  glossary: Glossary,
  subject: ReferenceSubject,
): ExpansionResult | null {
  const entities = getReferencedEntities(value);

  if (hasMalformedGlossaryReference(value)) {
    return {
      status: "malformed",
      entities,
      reason: `${subject} tiene referencias anidadas o sin cerrar`,
    };
  }

  const unknown = entities.find(name => !glossary[name]);

  if (unknown) {
    return {
      status: "unknown-entity",
      entities,
      reason: `${subject} referencia "${unknown}", que no está definido en la hoja Glosario`,
    };
  }

  return null;
}

/**
 * Replaces every reference in an authored text with the new terminology.
 *
 * Used both for a template and for a sentence the client wrote themselves — in the
 * latter case there is no app text to align against, so the casing map comes from the
 * row's template and anything it does not cover is handed back for review.
 */
export function resolveReferences({
  value,
  glossary,
  locale,
  casing = {},
}: ResolveOptions): ExpansionResult {
  const entities = getReferencedEntities(value);
  const invalid = validateReferences(value, glossary, "El texto");

  if (invalid) return invalid;

  const reasons: string[] = [];
  let text = "";
  let cursor = 0;

  [...value.matchAll(GLOSSARY_TOKEN)].forEach(match => {
    const entry = glossary[match[1]];
    const current = getGlossaryValue(entry, locale, "current");
    const next = getGlossaryValue(entry, locale, "new");
    const knownCasing = casing[match[1]];
    const before = value.slice(0, match.index);

    text += value.slice(cursor, match.index);
    text += applyCasing(next, knownCasing === undefined ? isCapitalised(next) : knownCasing);
    cursor = match.index + match[0].length;

    if (knownCasing === undefined) {
      reasons.push(
        `no se pudo deducir si "${entry.name}" va en mayúscula o minúscula en esta frase`,
      );

      return;
    }

    if (next === current) return;

    // An article before the term, or a term carrying its own capitals or spaces, means
    // the wording around it probably has to change too.
    if (TRAILING_ARTICLE.test(before)) {
      reasons.push(`"${entry.name}" va después de un artículo, revisar género y número`);
    }

    if (next.trim().includes(" ") || hasInternalCapital(next)) {
      reasons.push(`"${next}" trae mayúsculas o espacios propios, revisar cómo queda en la frase`);
    }
  });

  text += value.slice(cursor);

  return {
    status: reasons.length ? "needs-review" : "clean",
    text,
    entities,
    reason: reasons.length ? [...new Set(reasons)].join("; ") : undefined,
  };
}

interface ExpandOptions {
  template: string;
  appText: string;
  glossary: Glossary;
  locale: LocaleCode;
}

/** Rebuilds a template with the new terminology, refusing to guess when it cannot align. */
export function expandTemplate({
  template,
  appText,
  glossary,
  locale,
}: ExpandOptions): ExpansionResult {
  const entities = getReferencedEntities(template);
  const invalid = validateReferences(template, glossary, "La plantilla");

  if (invalid) return invalid;

  const casing = learnEntityCasing({ template, appText, glossary, locale });

  if (!casing) {
    return {
      status: "not-aligned",
      entities,
      reason:
        "La plantilla no calza con el texto actual de la app (normalmente por un plural), " +
        "así que el texto nuevo hay que redactarlo a mano",
    };
  }

  return resolveReferences({ value: template, glossary, locale, casing });
}
